import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertCircle, Clock, Sparkles, Gift } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTrialStatus } from "@/hooks/useTrialStatus";

export function TrialBanner() {
  const navigate = useNavigate();
  const { isTrialExpired, isTrialExpiringSoon, daysRemaining, hasActiveSubscription, isBetaUser } = useTrialStatus();

  // Beta user - show special banner
  if (isBetaUser && daysRemaining !== null) {
    return (
      <Alert className="border-2 border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20 shadow-lg">
        <Gift className="h-5 w-5 text-emerald-600 dark:text-emerald-500" />
        <AlertTitle className="text-lg font-bold text-emerald-900 dark:text-emerald-400">
          Beta Tester 🎉
        </AlertTitle>
        <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-emerald-800 dark:text-emerald-300">
          <span className="text-base">
            Thank you for being a beta tester! You have <span className="font-bold text-xl">{daysRemaining}</span> {daysRemaining === 1 ? "day" : "days"} of full access remaining.
          </span>
        </AlertDescription>
      </Alert>
    );
  }

  // Don't show banner if user has active subscription (non-beta)
  if (hasActiveSubscription) {
    return null;
  }

  // Trial expired - urgent message
  if (isTrialExpired) {
    return (
      <Alert variant="destructive" className="border-2 border-destructive shadow-lg">
        <AlertCircle className="h-5 w-5" />
        <AlertTitle className="text-lg font-bold">Trial Expired</AlertTitle>
        <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className="text-base">Your 14-day trial has ended. Upgrade now to continue using all features.</span>
          <Button 
            size="lg" 
            variant="secondary"
            onClick={() => navigate("/settings")}
            className="shrink-0"
          >
            Upgrade Now
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Trial expiring soon (3 days or less) - warning
  if (isTrialExpiringSoon) {
    return (
      <Alert className="border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/20 shadow-lg">
        <Clock className="h-5 w-5 text-amber-600 dark:text-amber-500" />
        <AlertTitle className="text-lg font-bold text-amber-900 dark:text-amber-400">
          Trial Ending Soon
        </AlertTitle>
        <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-amber-800 dark:text-amber-300">
          <span className="text-base">
            Your trial expires in <span className="font-bold text-xl">{daysRemaining}</span> {daysRemaining === 1 ? "day" : "days"}. 
            Upgrade to keep access to all features.
          </span>
          <Button 
            size="lg" 
            variant="secondary"
            onClick={() => navigate("/settings")}
            className="shrink-0"
          >
            View Plans
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Trial active - informational
  return (
    <Alert className="border-2 border-primary bg-primary/5 shadow-md">
      <Sparkles className="h-5 w-5 text-primary" />
      <AlertTitle className="text-lg font-bold text-primary">
        Free Trial Active
      </AlertTitle>
      <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <span className="text-base text-foreground">
          {daysRemaining !== null ? (
            <>
              You have <span className="font-bold text-xl text-primary">{daysRemaining}</span> {daysRemaining === 1 ? "day" : "days"} remaining in your free trial. 
            </>
          ) : (
            <>Your free trial is active. </>
          )}
          Explore all features risk-free!
        </span>
        <Button 
          size="lg" 
          onClick={() => navigate("/settings")}
          className="shrink-0"
        >
          Upgrade Early
        </Button>
      </AlertDescription>
    </Alert>
  );
}
